import React from "react";
import type { ProcessedBettingCompetitor } from "hooks/firestore/betting/useEvent";
import {
  isRacingCompetitor,
  type ProcessedRacingCompetitor,
} from "hooks/firestore/betting/useRacingEvents";
import type { PickemsCompetitor } from "hooks/firestore/models/pickems";
import type { OutcomeType } from "hooks/firestore/models/outcome";
import cx from "classnames";
import { Silks } from "@/features/race/components/Silks";
import { CompetitorAvatar } from "@/features/betting/components/CompetitorAvatar";
import {
  SubtractIcon,
  HigherIcon,
  LowerIcon,
  YesIcon,
  NoIcon,
  OddIcon,
  EvenIcon,
} from "@picklebet/ui";

import * as styles from "./OutcomeIcon.module.scss";

const iconComponents = {
  DRAW: SubtractIcon,
  OVER: HigherIcon,
  UNDER: LowerIcon,
  YES: YesIcon,
  NO: NoIcon,
  ODD: OddIcon,
  EVEN: EvenIcon,
};

type SportsIconProps = {
  outcomeType?: OutcomeType | null;
  competitor?: ProcessedBettingCompetitor | PickemsCompetitor;
  size?: number;
  className?: string;
  /** shows a transparent placeholder to maintain alignment */
  reserveAvatarSpace?: boolean;
};

/**
 * Renders the icon for a sports/esports outcome: a fixed glyph for the
 * icon-typed outcomes (draw/over/under/…) or the competitor's avatar otherwise.
 * For racing (silks) use the `OutcomeIcon` dispatcher below.
 */
const SportsIcon = ({
  outcomeType,
  competitor,
  size,
  className: classNameProp,
  reserveAvatarSpace = false,
}: SportsIconProps) => {
  const glyphSize = size ?? 20;

  // Special rule for outcomes where the type is DRAW
  // https://puntaa.atlassian.net/browse/PKB-4346
  if (outcomeType === "DRAW") {
    const DrawIcon = iconComponents.DRAW;
    return (
      <DrawIcon
        className={cx(styles.icon, classNameProp)}
        style={{
          minWidth: glyphSize,
          width: glyphSize,
          height: glyphSize,
        }}
      />
    );
  }

  const IconComponent = outcomeType
    ? iconComponents[outcomeType as keyof typeof iconComponents]
    : undefined;

  return IconComponent ? (
    <div
      className={cx(styles.icon, classNameProp)}
      style={{
        minWidth: glyphSize,
        width: glyphSize,
        height: glyphSize,
      }}
    >
      <IconComponent />
    </div>
  ) : (
    // default if icon type does not exist.
    <CompetitorAvatar
      className={cx(styles.avatar, classNameProp)}
      competitor={competitor}
      reserveAvatarSpace={reserveAvatarSpace}
      size={size}
    />
  );
};

type OutcomeIconProps = {
  outcomeType?: OutcomeType | null;
  competitor?:
    | ProcessedBettingCompetitor
    | ProcessedRacingCompetitor
    | PickemsCompetitor;
  silksUrl?: string | null;
  size?: number;
  className?: string;
  reserveAvatarSpace?: boolean;
};

/**
 * Outcome icon dispatcher: renders racing silks when a racing competitor and a
 * silks URL are available, otherwise falls back to the sports outcome icon.
 */
const OutcomeIcon = ({
  outcomeType,
  competitor,
  silksUrl,
  size,
  className,
  reserveAvatarSpace,
}: OutcomeIconProps) => {
  if (silksUrl && competitor && isRacingCompetitor(competitor)) {
    return (
      <Silks
        competitorNumber={competitor.number}
        imageHeight={size ?? 20}
        silksUrl={silksUrl}
        skeletonSize="xs"
        className={styles.silkSpacing}
        fallbackClassName={styles.silkSpacing}
      />
    );
  }

  return (
    <SportsIcon
      competitor={competitor}
      outcomeType={outcomeType}
      size={size}
      reserveAvatarSpace={reserveAvatarSpace}
      className={cx(styles.outcomeIcon, className)}
    />
  );
};

export { OutcomeIcon, SportsIcon };
